import { Button, Typography } from '@breetometer/components';
import { classNameMerger } from '@breetometer/utils';
import { CryptoSettingsIcon } from 'BreetAssets';
import { Checkbox, getReceiveCurrencyLabel, InfoArticle, Loader, QueryBoundary } from 'BreetComponents';
import { capitalizeFirstLetter, cryptoSettingsOptions, type ReceiveCurrencyType } from 'BreetHelpers';
import { useCryptoSettings } from 'BreetHooks';
import { type ChangeEvent, useCallback } from 'react';

const CryptoSettings = ({ isModal }: { isModal?: boolean }) => {
	const {
		handleCurrencyChange,
		handleSaveChanges,
		hasUnsavedChanges,
		isUpdating,
		isLoading,
		selectedReceiveCurrency,
		saveChangesRef,
		fiatMeta,
		currentReceiveCurrency,
		isError,
		hasData,
		refetch,
	} = useCryptoSettings(isModal);

	const handleRetry = useCallback(() => refetch(), [refetch]);

	const handleOnChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const newReceiveCurrency = e.target.value as ReceiveCurrencyType;
			handleCurrencyChange(newReceiveCurrency);
		},
		[handleCurrencyChange]
	);

	const instanceSuffix = isModal ? '-modal' : '';

	if (isLoading) {
		return <Loader />;
	}

	return (
		<div
			className={classNameMerger(
				'cryptosettings scrollbar subbodycontent_main bg-primary-500',
				!isModal ? 'border-dark-navy' : 'modal'
			)}
		>
			<Typography
				className='color-neutral-50'
				variant='sub_head'
			>
				Which wallet should receive your crypto?
			</Typography>
			<QueryBoundary
				isLoading={false}
				isError={isError}
				hasData={hasData}
				onRetry={handleRetry}
				title="We couldn't load your crypto settings"
				description="We can't confirm your receiving wallet right now. Please try again."
			>
				<div className='cryptosettings_list'>
					{cryptoSettingsOptions
						.filter((option) => {
							const fiatCurrencies = ['ngn', 'ghs'] as const;
							const isFiatOption = fiatCurrencies.includes(option.currency as (typeof fiatCurrencies)[number]);
							return !isFiatOption || option.currency === fiatMeta.currencyLower;
						})
						.map((option) => {
							const isActive = option.currency === selectedReceiveCurrency;

							return (
								<div
									key={option.currency}
									className={classNameMerger('cryptosettings_list_item border-dark-navy', isActive && 'active bg-primary-400')}
								>
									<label
										htmlFor={`${option.currency}${instanceSuffix}`}
										className='cryptosettings_list_item_label'
										aria-label={`${capitalizeFirstLetter(getReceiveCurrencyLabel(option.currency))} Wallet`}
									/>
									<div className='cryptosettings_list_item_head'>
										<CryptoSettingsIcon variant={option.currency} />
										<Checkbox
											id={`${option.currency}${instanceSuffix}`}
											type='radio'
											name={`receiveCurrency${instanceSuffix}`}
											checked={isActive}
											value={option.currency}
											onChange={handleOnChange}
										/>
									</div>
									<div className='cryptosettings_list_item_content'>
										<div className='cryptosettings_list_item_content_title'>
											<Typography
												className='title'
												variant='paragraph_1'
											>
												{capitalizeFirstLetter(getReceiveCurrencyLabel(option.currency))} Wallet
											</Typography>
											{currentReceiveCurrency === option.currency && (
												<span className='activebadge'>
													<Typography
														as='span'
														variant='paragraph_2'
													>
														Active
													</Typography>
												</span>
											)}
										</div>
										<Typography
											variant='paragraph_2'
											className={classNameMerger('description', isActive && 'color-primary-100')}
										>
											{option.description}
										</Typography>
									</div>
								</div>
							);
						})}
				</div>
				<div className='cryptosettings_cta'>
					<InfoArticle
						articleNumber='13925878'
						text='How it works'
					/>
					<Button
						size='lg'
						ref={saveChangesRef}
						className='cryptosettings_cta_update'
						disabled={!hasUnsavedChanges || isUpdating}
						isLoading={isUpdating}
						onClick={handleSaveChanges}
					>
						Save Changes
					</Button>
				</div>
			</QueryBoundary>
		</div>
	);
};

export default CryptoSettings;
